/**
 * Per-organization permissions, mirroring backend/app/core/permissions.py.
 *
 * `/api/v2/auth/me` returns `memberships: [{organization_id, organization_name, roles, permissions}]`.
 * What the user may do depends on the organization selected in the header pulldown, so every
 * helper here takes that org id. The backend enforces all of this; the frontend only uses it to
 * hide what a role cannot do.
 */

export const PERMISSIONS = Object.freeze({
  DASHBOARD_VIEW: 'dashboard.view',
  OFFERS_VIEW: 'offers.view',
  FASTID_VIEW: 'fastid.view',
  FASTID_RUN: 'fastid.run',
  UNIVERSE_UPLOAD: 'universe.upload',
  BULK_SUBMIT: 'bulk.submit',
  SEGMENTS_VIEW: 'segments.view',
  SEGMENTS_CREATE: 'segments.create',
  DATA_QUERY: 'data.query',
  DATA_REQUEST: 'data.request',
  EXPORT: 'export',
  API_ACCESS: 'api.access',
  ORG_SETTINGS: 'org.settings',
  ORG_USERS: 'org.users',
});

/**
 * Permission each route needs in the selected organization. `null` means any signed-in user.
 * `platformAdmin: true` routes are for platform admins (`user.is_admin`) only.
 * Order matters: it is the fallback order when a user is redirected off a page they cannot see.
 */
export const PAGE_ACCESS = [
  { path: '/dashboard', permission: PERMISSIONS.DASHBOARD_VIEW },
  { path: '/offer-report', permission: PERMISSIONS.OFFERS_VIEW, prefix: true },
  { path: '/fastid-report', permission: PERMISSIONS.FASTID_VIEW, prefix: true },
  { path: '/file-upload', permission: PERMISSIONS.UNIVERSE_UPLOAD },
  { path: '/segments', permission: PERMISSIONS.SEGMENTS_VIEW, prefix: true },
  { path: '/chat', permission: PERMISSIONS.DATA_QUERY },
  { path: '/query', permission: PERMISSIONS.DATA_QUERY },
  { path: '/file-download', permission: PERMISSIONS.DATA_REQUEST },
  { path: '/signals', permission: PERMISSIONS.DATA_REQUEST },
  { path: '/api-keys', permission: PERMISSIONS.API_ACCESS },
  { path: '/api-usage', permission: PERMISSIONS.API_ACCESS },
  { path: '/docs', permission: PERMISSIONS.API_ACCESS },
  { path: '/organization-properties', permission: PERMISSIONS.ORG_SETTINGS },
  { path: '/organization-members', permission: PERMISSIONS.ORG_USERS },
  { path: '/bulk-upload', permission: PERMISSIONS.BULK_SUBMIT },
  { path: '/data-dictionary', platformAdmin: true },
  { path: '/admin', platformAdmin: true },
  { path: '/profile', permission: null },
];

/** @returns {Array} memberships from /me, or [] */
export function membershipsOf(user) {
  return Array.isArray(user?.memberships) ? user.memberships : [];
}

/** Find the membership for an org id (string or number). */
export function membershipForOrg(user, orgId) {
  if (orgId == null || orgId === '') return null;
  const wanted = String(orgId);
  return membershipsOf(user).find((m) => String(m.organization_id) === wanted) || null;
}

/** Permissions the user holds while acting as `orgId`. Platform admins hold every permission. */
export function permissionsForOrg(user, orgId) {
  const membership = membershipForOrg(user, orgId);
  if (membership) return new Set(membership.permissions || []);
  if (user?.is_admin) {
    // A platform admin outside their memberships (e.g. admin panel context) still passes.
    return new Set(Object.values(PERMISSIONS));
  }
  return new Set();
}

export function hasPermission(user, orgId, permission) {
  if (permission == null) return Boolean(user);
  return permissionsForOrg(user, orgId).has(permission);
}

/** Route rule for a pathname (prefix routes like /segments/123 match their parent). */
export function pageAccessFor(pathname) {
  return (
    PAGE_ACCESS.find((p) => (p.prefix ? pathname === p.path || pathname.startsWith(`${p.path}/`) : pathname === p.path)) ||
    null
  );
}

export function canAccessPage(user, orgId, pathname) {
  const rule = pageAccessFor(pathname);
  if (!rule) return Boolean(user); // unknown routes are not gated here
  if (rule.platformAdmin) return Boolean(user?.is_admin);
  return hasPermission(user, orgId, rule.permission);
}

/** First page (in PAGE_ACCESS order) the user may open in this org; /profile as a last resort. */
export function firstAllowedPath(user, orgId) {
  const hit = PAGE_ACCESS.find((p) => p.path !== '/profile' && canAccessPage(user, orgId, p.path));
  return hit ? hit.path : '/profile';
}

/** Selected org id from the cookie, falling back to the user's first membership. */
export function resolveSelectedOrgId(user, cookieOrgId) {
  if (cookieOrgId != null && cookieOrgId !== '' && membershipForOrg(user, cookieOrgId)) {
    return String(cookieOrgId);
  }
  const first = membershipsOf(user)[0];
  return first ? String(first.organization_id) : null;
}
